import React, { useState } from 'react';
import '../../css/CueCumberLanding.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faCamera, faChartLine, faUsers, faShieldAlt, faMobile, faCloudUploadAlt, faFileExport, faSearch } from '@fortawesome/free-solid-svg-icons';
import {useProject} from "../../utils/ProjectContext";

const CueCumberLanding = () => {
    const [isFormCollapsed, setIsFormCollapsed] = useState(true);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        message: ''
    });
    const { BASE_URL} = useProject();
    const [showThankYouModal, setShowThankYouModal] = useState(false);

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const response = await fetch(`${BASE_URL}/contact`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    firstName: formData.name.split(' ')[0],
                    lastName: formData.name.split(' ')[1] || '',
                    email: formData.email,
                    message: formData.message || 'Early access request'
                })
            });

            if (response.ok) {
                setFormData({
                    name: '',
                    email: '',
                    message: ''
                });
                setShowThankYouModal(true);
                toggleForm();
                setTimeout(() => {
                    setShowThankYouModal(false);
                }, 3000);
            }
        } catch (error) {
            console.error('Error sending message:', error);
        }
    };

    const toggleForm = () => {
        setIsFormCollapsed(!isFormCollapsed);
    };

    return (
        <div className="cuecumber-landing">
            {/* Hero Section */}
            <section className="hero-section">
                <div className="hero-content">
                    <div className="trust-strip">
                        Early access • iOS/Android beta • Built with clinicians & families • Privacy-first
                    </div>
                    <h1>Guide the day. Capture the progress.</h1>
                    <p className="hero-subhead">
                        CueCumber helps families and schools use visual supports while quietly capturing opt-in reactions and notes—so everyone sees what's working.
                    </p>
                    <div className="cta-buttons">
                        <button className="cta-primary" onClick={toggleForm}>Get Early Access</button>
                        <button className="cta-secondary">See How It Works</button>
                    </div>
                </div>
                <div className="hero-image">
                    <img
                        src={process.env.PUBLIC_URL + '/cuecumber.png'}
                        alt="CueCumber App Preview"
                    />
                </div>
            </section>

            {/* How It Works Section */}
            <section className="how-it-works">
                <h2>How CueCumber works</h2>
                <div className="steps">
                    <div className="step">
                        <div className="step-number">1</div>
                        <h3>Show a cue</h3>
                        <p>Snap or pick images to build Choices, First/Then, Social Stories or Schedules in seconds.</p>
                    </div>
                    <div className="step">
                        <div className="step-number">2</div>
                        <h3>Capture context (automatically)</h3>
                        <p>With opt-in camera reactions, device movement, and quick tap notes, CueCumber logs what happened and when.</p>
                    </div>
                    <div className="step">
                        <div className="step-number">3</div>
                        <h3>See the story</h3>
                        <p>Get auto-generated summaries by topic, progress over time, and therapist-ready reports you can share.</p>
                    </div>
                </div>
            </section>

            {/* Core Features Section */}
            <section className="features">
                <h2>Core features</h2>
                <div className="features-grid">
                    <div className="feature">
                        <FontAwesomeIcon icon={faMobile} className="feature-icon" />
                        <h3>Instant visual supports</h3>
                        <p>Build Choices, First/Then, Social Stories, and day plans from your own photos or library items.</p>
                    </div>
                    <div className="feature">
                        <FontAwesomeIcon icon={faCamera} className="feature-icon" />
                        <h3>Opt-in reaction tracking</h3>
                        <p>Use the front camera to capture facial/attention signals during cues. Fully optional.</p>
                    </div>
                    <div className="feature">
                        <FontAwesomeIcon icon={faUsers} className="feature-icon" />
                        <h3>Shared language</h3>
                        <p>Invite family, teachers, and carers; everyone sees the same cues and updates.</p>
                    </div>
                    <div className="feature">
                        <FontAwesomeIcon icon={faChartLine} className="feature-icon" />
                        <h3>Reports therapists love</h3>
                        <p>Topic summaries, behaviour snapshots, overview charts, and time-of-day patterns—exportable to PDF.</p>
                    </div>
                    <div className="feature">
                        <FontAwesomeIcon icon={faCloudUploadAlt} className="feature-icon" />
                        <h3>Works offline, syncs later</h3>
                        <p>Designed for real life—log now, upload when connected.</p>
                    </div>
                    <div className="feature">
                        <FontAwesomeIcon icon={faSearch} className="feature-icon" />
                        <h3>Searchable "meaning map"</h3>
                        <p>Save associations like "when they say X, they mean Y" so new carers can understand faster.</p>
                    </div>
                </div>
            </section>

            {/* What Makes Us Different Section */}
            <section className="differentiators">
                <h2>What makes CueCumber different</h2>
                <div className="differentiator-list">
                    <div className="differentiator">
                        <strong>Parent-first speed</strong> → create useful cues in seconds, not a planning session.
                    </div>
                    <div className="differentiator">
                        <strong>Evidence-friendly data</strong> → structured logs and summaries that turn lived moments into usable insights.
                    </div>
                    <div className="differentiator">
                        <strong>Context, not surveillance</strong> → opt-in, focused reaction snapshots during intentional sessions, with clear consent and controls.
                    </div>
                    <div className="differentiator">
                        <strong>Shared ecosystem</strong> → home, school and therapy aligned with the same visual language.
                    </div>
                </div>
            </section>

            {/* Audience Tabs Section */}
            <section className="audience-tabs">
                <h2>Built for everyone who supports the child</h2>
                <div className="audience-content">
                    <div className="audience-section">
                        <h3>For parents & carers</h3>
                        <ul>
                            <li>Guide mornings, choices, and transitions</li>
                            <li>See which supports land best over time</li>
                            <li>Arrive at therapy with a clear record, not guesswork</li>
                        </ul>
                    </div>
                    <div className="audience-section">
                        <h3>For teachers</h3>
                        <ul>
                            <li>Consistent supports across class and home</li>
                            <li>Quick note capture that turns into weekly snapshots</li>
                            <li>Works with terms and timetables for contextual reports</li>
                        </ul>
                    </div>
                    <div className="audience-section">
                        <h3>For speech pathologists & therapists</h3>
                        <ul>
                            <li>Session-quality summaries without extra admin</li>
                            <li>Exportable PDFs and CSVs</li>
                            <li>Track progress by topic and by time of day, not just by session</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* Privacy & Consent Section */}
            <section className="privacy">
                <FontAwesomeIcon icon={faShieldAlt} className="privacy-icon" />
                <h2>Privacy & consent</h2>
                <div className="privacy-points">
                    <p><strong>You're in control:</strong> Reaction tracking and logging are opt-in and per-session.</p>
                    <p><strong>Minimal, meaningful data:</strong> We store only what's needed for your reports.</p>
                    <p><strong>Transparency:</strong> Clear settings for what's captured and who can access it.</p>
                    <p><strong>Not a medical device:</strong> CueCumber supports care; it doesn't diagnose.</p>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="faq">
                <h2>Frequently Asked Questions</h2>
                <div className="faq-items">
                    <div className="faq-item">
                        <h4>Is CueCumber an AAC app?</h4>
                        <p>It's a companion for AAC-style visual supports. It focuses on rapid visuals, shared language, and progress tracking rather than full-vocabulary speech generation.</p>
                    </div>
                    <div className="faq-item">
                        <h4>Do I have to use the camera?</h4>
                        <p>No. Reaction tracking is 100% optional and controlled by you.</p>
                    </div>
                    <div className="faq-item">
                        <h4>Can I share with teachers and grandparents?</h4>
                        <p>Yes. Invite people you trust and choose what each person can see.</p>
                    </div>
                    <div className="faq-item">
                        <h4>Can I export reports for therapy or NDIS reviews?</h4>
                        <p>Yes—PDF and CSV exports make it easy to share progress.</p>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="landing-footer">
                <p>
                    Built by{' '}
                    <a
                        href="https://www.edmoss.com.au"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Edmoss
                    </a>
                </p>
            </footer>

            {/* Contact Form Overlay */}
            <div
                className={`contact-form-overlay ${isFormCollapsed ? 'collapsed' : ''}`}
                onClick={() => isFormCollapsed && toggleForm()}
            >
                {!isFormCollapsed && (
                    <button
                        className="close-button"
                        onClick={(e) => {
                            e.stopPropagation();
                            toggleForm();
                        }}
                    >
                        ✕
                    </button>
                )}
                <h3>
                    {isFormCollapsed ? (
                        <>
                            <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: '10px' }} />
                            Get Early Access
                        </>
                    ) : (
                        'Get Early Access'
                    )}
                </h3>
                <div className={`contact-form-content ${isFormCollapsed ? 'hidden' : ''}`}>
                    <form onSubmit={handleSubmit} onClick={(e) => e.stopPropagation()}>
                        <input
                            type="text"
                            name="name"
                            placeholder="Your Name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                        />
                        <input
                            type="email"
                            name="email"
                            placeholder="Your Email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                        />
                        <textarea
                            name="message"
                            placeholder="Tell us about your needs (optional)"
                            value={formData.message}
                            onChange={handleInputChange}
                            rows="3"
                        />
                        <button type="submit">Join Early Access</button>
                    </form>
                </div>
            </div>

            {/* Thank You Modal */}
            {showThankYouModal && (
                <>
                    <div className="modal-overlay" />
                    <div className="thank-you-modal">
                        <div className="modal-content">
                            <h3>Welcome to CueCumber! 🎉</h3>
                            <p>Thank you for your interest in early access.</p>
                            <p>We'll be in touch soon with next steps.</p>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default CueCumberLanding;